import React, { CSSProperties, MutableRefObject, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { AddAnimalToPen, ProgressBar } from "../.."
import { colors, fonts, glyphs, } from "../../../Styles"

import { useAppSelector } from '../../../Store';
import { Graph_QL } from '../../../Services';
import { useAnimal } from '../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal';
import { LfLocation } from '../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall';
import { PanelMaintainAnimal } from '../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/maintainAnimalInfoCall';
import { keyBy, merge } from 'lodash';

interface PenAdd_Props {
  pen?: LfLocation; 
  hasHoverEffect?: boolean
  dirtyDataRef: MutableRefObject<PanelMaintainAnimal[]>
}
/**
 * PEN_ADD
 * @param penId Unique identifier for each pen.
 * @param hasHoverEffect Boolean that, if true, adds a hover effect to the pen.
 * @returns a pen where onMouseEnter adds a hoverEffect on empty pens.
 */
export default function PEN_ADD({ pen, hasHoverEffect, dirtyDataRef }: PenAdd_Props) {
  const {t} = useTranslation()
  const [isHovered, setIsHovered] = useState(false);
  const [showAddAnimalToPenModal, setAddAnimalToPenModal] = useState(false)
  const [selectedAnimal, setAnimal] = useState<number | undefined>();
  
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({ domain: farm })), [farm]);
  
  const {  call: getAnimalsInfo, data: animalsState } = useAnimal(connection)
  
    useEffect(() => {
    if (!(animalsState[domain])) {
      getAnimalsInfo({fetchAmount: 50}, connection);
    }
  }, [animalsState, connection, domain, getAnimalsInfo])

  // #region | Data handling
  // New Animal
  const newAnimal = useMemo(() => {
    if (!selectedAnimal) return undefined;
    return animalsState[domain]?.data.find(a => a.animal_id === selectedAnimal)?.animal_number;
  }, [domain, animalsState, selectedAnimal]);

  // Animalinfo if animal already excists in pen
  const animalInfo = useMemo(() => {
    const animal = pen?.animals && pen.animals.length > 0 ? pen.animals[0] : undefined
    const currentAnimal = dirtyDataRef.current.find(a => a.animalId === animal?.animal_id);

    // Feed Correction
    const feedCorrection = animal
    ? animal.correction === 0 ||
      animal.correction === undefined ||
      animal.correction === null
      ? "0%"
      : animal.correction + "%"
    : "0%";
      
    // Milestone
    const milestoneDayNo = animal?.milestone_day
    ? Math.floor((new Date().getTime() - animal.milestone_day) / (1000 * 60 * 60 * 24))
    : t("noData", { defaultValue: "no data" });
      
    const milestoneName = animal?.milestone?.name ?? "";
    
    if (animal) {
      dirtyDataRef.current = Object.values(
        merge({}, keyBy(dirtyDataRef.current, "animalId"), keyBy([{
          ...currentAnimal,
          animalId: animal?.animal_id,
          location: animal?.location_id,
        }], "animalId"))
      );
    }

    return {
      feedCorrection,
      milestoneDayNo,
      milestoneName,
      animal
    };
  }, [dirtyDataRef, pen?.animals, t])

  // ConsumptionProgressBar
  const ConsumptionProgressBar: JSX.Element = useMemo(() => {
    const progress = (animalInfo.animal?.consumptions?.[0])
    const progressBarTotal = (progress?.total ?? 0) / 1000;
    const progressBarProgressed = (progress?.progressed ?? 0) / 1000;

    return (
      <ProgressBar
        total={+(progressBarTotal).toFixed(2)}
        progressed={+(progressBarTotal - progressBarProgressed).toFixed(2)}
      />
    );
  }, [animalInfo.animal?.consumptions])
  // #endregion

  // Update animal
  useEffect(() => {
    if (selectedAnimal) {
      const current = dirtyDataRef.current.find(a => a.animalId === animalInfo.animal?.animal_id);
      const updatedAnimal: PanelMaintainAnimal = {
        ...(current ?? animalInfo.animal),
        animalId: selectedAnimal,
        location: pen?.location_id,
      };
  
      dirtyDataRef.current = Object.values(
        merge({},
          keyBy(dirtyDataRef.current, "animalId"),
          keyBy([updatedAnimal], "animalId")
        )
      );
    }
  }, [animalInfo.animal, dirtyDataRef, pen?.location_id, selectedAnimal]);

  // #region | Content
  // Advanced content to parse in the div
  const advancedContent = <>
    { selectedAnimal !== undefined ? 
      <div style={{ cursor: "pointer", display: "grid", gridTemplateColumns: "22px 45px 521px 22px", textAlign: "start", alignItems: "center", gap: "5px" }}>
        <span></span>
          <span style={{}}>{pen?.name}</span>
          <span style={{display: "flex", alignItems: "center", gap: "10px"}}><glyphs.Arrow fillColor={colors.greyDarkPrimary} rotate={180}/>Animal {newAnimal}</span>
        <span></span>
      </div>
    : animalInfo.animal ? 
      <div style={{ display: "grid", gridTemplateColumns: "22px 45px 45px 108px 45px 108px 45px 100px 45px 22px", textAlign: "start", alignItems: "center", gap: "5px" }}>
        <span></span>
        <span style={{filter: "opacity(0.5)", cursor: "default"}}>{pen?.name}</span>
        <span style={{filter: "opacity(0.5)", cursor: "default"}}>{animalInfo.animal.animal_number}</span>
        <span style={{filter: "opacity(0.5)", cursor: "default"}}>{animalInfo.milestoneName}</span>
        <span style={{filter: "opacity(0.5)", cursor: "default"}}>{animalInfo.milestoneDayNo}</span>
        <span style={{filter: "opacity(0.5)", cursor: "default", paddingLeft: "5px"}}>{animalInfo.animal.feedCurve?.name}</span>
        <span style={{filter: "opacity(0.5)", cursor: "default", paddingLeft: "5px"}}>{animalInfo.feedCorrection}</span>
        <span style={{filter: "opacity(0.5)", cursor: "default"}}> {ConsumptionProgressBar} </span>
        <span></span>
        <span></span>
      </div>
    : 
      <div style={{ display: "grid", gridTemplateColumns: "22px 45px 521px 22px", textAlign: "start", alignItems: "center", gap: "5px" }}>
        <span></span>
        <span style={{cursor: "pointer"}}>{pen?.name}</span>
        <span style={{cursor: "pointer"}}>{t("locationFeederPages.sections.emptyPen", {defaultValue: "Empty pen"})}</span>
        <span></span>
      </div>
    }
  </>
  // #endregion

  return (
    <>
      <div 
        onClick={!animalInfo.animal ? () => setAddAnimalToPenModal(true) : undefined}
        onMouseEnter={(hasHoverEffect && !animalInfo.animal) ? () => setIsHovered(true) : undefined}
        onMouseLeave={(hasHoverEffect && !animalInfo.animal) ? () => setIsHovered(false) : undefined}
        style={{ 
          width: "650px",
          height: "35px",
          cursor: hasHoverEffect && !animalInfo.animal? "pointer" : "", 
          boxSizing: "border-box"
      }}>
        <div style={{
          width: "100%",
          height: "100%",
          textAlign: 'center',
          alignContent: "center",
          boxSizing: "border-box",
          padding: "0px 10px",
          backgroundColor: isHovered ? colors.greyLightPrimary : colors.greyLightSecondary,
          transition: 'filter 0.3s',
          color: colors.greyDarkPrimary,
          ...fonts.paragraph,
          ...fonts.fontSize4,
        }}>
          {advancedContent}
        </div>
      </div>
      {/* Add Animal Modal */}
      <AddAnimalToPen
        isModalOpen={showAddAnimalToPenModal}
        activePen={pen}
        goBack={() => setAddAnimalToPenModal(false)}
        onChange={(id) => {
          setAnimal(id);
        }}
      />
    </>
  );
}